/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/SortableFileItem.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Sortable file item component for MOH Sources file lists.
    Supports drag & drop reordering and audio preview.
────────────────────────────────────────────────────────────────*/

import { GripVertical, Volume2, Play, Pause, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../../components/ui/button';
import { SortableFileItemProps } from '../types';
import { formatFileSizeMB } from '../utils';

/**
 * Sortable File Item Component
 */
export function SortableFileItem({
  id,
  file,
  previewUrl,
  isCurrentPlaying,
  isPlaying,
  playMode,
  onPlay,
  onRemove,
  fileSize,
  isExisting = false,
  t,
}: SortableFileItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: playMode !== 'linear', // Only draggable when play_mode is 'linear'
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: playMode === 'linear' ? 'grab' : 'default',
  };

  const fileName = file instanceof File ? file.name : (typeof file === 'string' ? file : file.name);
  const displaySize = fileSize ? formatFileSizeMB(fileSize) : '';
  const canPlay = previewUrl || isExisting; // Existing files can be played (loaded on demand)

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-border hover:bg-muted/50">
      {/* Drag Handle */}
      <td className="py-3 px-2 text-center" style={{ width: '5%' }}>
        {playMode === 'linear' && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-2 text-muted-foreground hover:text-foreground rounded hover:bg-muted/50"
            aria-label={t('common:dragToReorder') || 'Drag to reorder'}
          >
            <GripVertical className="h-4 w-4" />
          </div>
        )}
        {playMode !== 'linear' && (
          <span className="text-muted-foreground text-xs">-</span>
        )}
      </td>

      {/* File Name */}
      <td className="py-3 px-4" style={{ width: '35%' }}>
        <div className="flex items-center space-x-2">
          <Volume2
            className={`h-4 w-4 ${file instanceof File ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <span className="text-sm font-medium">{fileName}</span>
        </div>
      </td>

      {/* File Size */}
      <td className="py-3 px-4 text-sm text-muted-foreground" style={{ width: '15%' }}>
        {displaySize || '-'}
      </td>

      {/* Status */}
      <td className="py-3 px-4 text-center text-xs text-muted-foreground" style={{ width: '15%' }}>
        {isExisting ? (
          <span className="text-muted-foreground">
            {t('mohSources.fields.onServer') || 'On server'}
          </span>
        ) : (
          <span className="text-primary">New</span>
        )}
      </td>

      {/* Play Button */}
      <td className="py-3 px-4 text-center" style={{ width: '10%' }}>
        {canPlay && (
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onPlay}
            aria-label={isPlaying ? t('common:pause') || 'Pause' : t('common:play') || 'Play'}
            className="h-9 px-2"
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
        )}
      </td>

      {/* Actions (Remove) */}
      <td className="py-3 px-4 text-center" style={{ width: '10%' }}>
        <Button
          type="button"
          onClick={onRemove}
          variant="outline"
          size="md"
          className="text-destructive hover:text-destructive h-9 px-2"
          aria-label={t('common:remove') || 'Remove file'}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

